// packages/core/src/sync/SyncEngineImpl.ts
// ─────────────────────────────────────────────────────────────────────────
// Concrete SyncEngine. Owns the local operation queue (via StorageAdapter) and
// replicates it to the server (via HttpSyncClient), gated by NetworkAdapter.
//
// QUEUE MODEL
//   STORAGE_TABLES.SYNC_QUEUE holds operations not yet confirmed by the server,
//   keyed by operation id. On a successful push/economy-apply, the op is moved
//   to STORAGE_TABLES.OPERATIONS (the local mirror of server-confirmed state)
//   and removed from the queue. Rejected ops are marked 'rejected' and left in
//   OPERATIONS for inspection, never re-pushed.
//
// THE FAN-OUT (load-bearing)
//   The server REJECTS economy.* ops in the operation log; balances live in a
//   separate transactional ledger. So push() splits the queue:
//     • economy.earn / economy.spend / economy.iap_purchase → applyEconomy()
//     • everything else                                      → pushOperations()
//   Getting this wrong means either lost gems or rejected pushes, so it's the
//   one branch to never "simplify".
//
// IDEMPOTENCY
//   Every op already carries a stable idempotencyKey from rewardKeys(). The
//   server's unique (userId, key) index makes re-pushing a duplicate a no-op
//   success, so an interrupted push that half-committed is safe to re-run.
//
// PULL
//   pull(since?) walks the server cursor (nextSince) until caught up, writing
//   other-device ops into OPERATIONS and advancing a persisted watermark. It
//   reports how many ops were applied. NOTE: turning pulled ops back into
//   derived UI state (XP, progress) is the APP's job — see applyPulled below.
// ─────────────────────────────────────────────────────────────────────────

import { STORAGE_TABLES, type StorageAdapter } from '../storage/StorageAdapter';
import type { NetworkAdapter } from '../storage/NetworkAdapter';
import type {
	Operation,
	OperationType,
	SyncEngine,
	SyncPullResult,
	SyncPushResult,
	SyncResult,
} from './SyncTypes';
import {
	HttpSyncClient,
	type CurrencyWire,
	type EconomyKindWire,
	type PulledOperationWire,
	type WireOperation,
} from './HttpSyncClient';

// Where the pull watermark (last serverTimestamp pulled) is persisted.
const PULL_CURSOR_KEY = 'sync.pullCursor';

// ── Helpers ────────────────────────────────────────────────────────────────

const ECONOMY_TYPES: ReadonlySet<OperationType> = new Set<OperationType>([
	'economy.earn',
	'economy.spend',
	'economy.iap_purchase',
]);

function isEconomyType(t: OperationType): boolean {
	return ECONOMY_TYPES.has(t);
}

/**
 * UUID without depending on the DOM lib. Reads crypto.randomUUID off the host
 * (browsers, Node 19+, modern RN all have it). Hosts that lack it should pass
 * a `uuid` seam into the engine.
 */
function defaultUuid(): string {
	const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
	if (c && typeof c.randomUUID === 'function') return c.randomUUID();
	throw new Error(
		'crypto.randomUUID unavailable; pass a uuid seam to SyncEngineImpl.',
	);
}

/** Map an economy operation type to the wire kind the /economy endpoint wants. */
function economyKind(t: OperationType): EconomyKindWire {
	switch (t) {
		case 'economy.earn':
			return 'earn';
		case 'economy.spend':
			return 'spend';
		case 'economy.iap_purchase':
			return 'iap';
		default:
			// Unreachable: callers gate on isEconomyType first.
			throw new Error(`not an economy type: ${t}`);
	}
}

/**
 * Economy payloads carry the currency and (for iap) the receipt. We keep this
 * tolerant: the reward dispatcher is the source of these fields, and we don't
 * want a malformed payload to wedge the whole queue.
 */
interface EconomyPayload {
	currency?: CurrencyWire;
	amount?: number; // optional override; defaults to payload-less amount below
	receipt?: unknown;
}

function toWireOperation(op: Operation): WireOperation {
	return {
		id: op.id,
		deviceId: op.deviceId,
		type: op.type,
		entityId: op.entityId,
		idempotencyKey: deriveIdempotencyKey(op),
		payload: op.payload,
		clientTimestamp: op.clientTimestamp,
	};
}

/**
 * The server's idempotency key. The reward pipeline already stamps a stable key
 * into entityId-adjacent state via rewardKeys(); we carry it on the operation
 * as `idempotencyKey` when present, else fall back to a deterministic
 * type:entityId form so the unique index still dedupes re-pushes.
 */
function deriveIdempotencyKey(op: Operation): string {
	const explicit = (op as Operation & { idempotencyKey?: string })
		.idempotencyKey;
	return explicit && explicit.length > 0
		? explicit
		: `${op.type}:${op.entityId}`;
}

// ── Engine ───────────────────────────────────────────────────────────────

export interface SyncEngineDeps {
	storage: StorageAdapter;
	network: NetworkAdapter;
	client: HttpSyncClient;
	/** Stable per-install device id (used on every recorded op). */
	deviceId: string;
	/** Current user id; sync no-ops when null (signed out). */
	getUserId: () => string | null;
	/**
	 * OPTIONAL. Called with operations pulled from other devices so the app can
	 * fold them into derived state (XP, progress, SRS). If omitted, pulled ops
	 * are still persisted to OPERATIONS but no UI state is updated — wire this
	 * to your reducers to close the multi-device loop.
	 */
	applyPulled?: (ops: PulledOperationWire[]) => Promise<void> | void;
	/** Clock seam for tests. Defaults to Date.now. */
	now?: () => number;
	/** UUID seam for tests. Defaults to crypto.randomUUID. */
	uuid?: () => string;
}

export class SyncEngineImpl implements SyncEngine {
	private readonly s: StorageAdapter;
	private readonly net: NetworkAdapter;
	private readonly client: HttpSyncClient;
	private readonly deviceId: string;
	private readonly getUserId: () => string | null;
	private readonly applyPulled?: SyncEngineDeps['applyPulled'];
	private readonly now: () => number;
	private readonly uuid: () => string;

	/** Guards against overlapping sync() runs stepping on the same queue. */
	private inFlight: Promise<SyncResult> | null = null;

	constructor(deps: SyncEngineDeps) {
		this.s = deps.storage;
		this.net = deps.network;
		this.client = deps.client;
		this.deviceId = deps.deviceId;
		this.getUserId = deps.getUserId;
		this.applyPulled = deps.applyPulled;
		this.now = deps.now ?? (() => Date.now());
		this.uuid = deps.uuid ?? defaultUuid;
	}

	// ── record ───────────────────────────────────────────────────────────

	async record<T>(
		op: Omit<Operation<T>, 'id' | 'clientTimestamp' | 'status'>,
	): Promise<Operation<T>> {
		const userId = this.getUserId() ?? op.userId;
		const full: Operation<T> = {
			...op,
			id: this.uuid(),
			userId,
			deviceId: op.deviceId || this.deviceId,
			clientTimestamp: this.now(),
			status: 'pending',
		};
		await this.s.set(STORAGE_TABLES.SYNC_QUEUE, full.id, full);
		return full;
	}

	// ── push ───────────────────────────────────────────────────────────────

	async push(): Promise<SyncPushResult> {
		const empty: SyncPushResult = {
			attempted: 0,
			synced: 0,
			rejected: 0,
			rejections: [],
		};

		if (!this.getUserId()) return empty;
		if (!this.net.isOnline()) return empty;

		const queued = await this.s.listValues<Operation>(
			STORAGE_TABLES.SYNC_QUEUE,
		);
		if (queued.length === 0) return empty;

		// Split by destination. Order within each bucket is preserved by
		// clientTimestamp so spends apply after the earns that fund them.
		const ordered = [...queued].sort(
			(a, b) => a.clientTimestamp - b.clientTimestamp,
		);
		const economy = ordered.filter((o) => isEconomyType(o.type));
		const operations = ordered.filter((o) => !isEconomyType(o.type));

		const result: SyncPushResult = { ...empty, attempted: ordered.length };

		// 1) Operation log — one batched call.
		if (operations.length > 0) {
			const wire = operations.map(toWireOperation);
			const resp = await this.client.pushOperations(wire);
			result.synced += resp.synced;
			result.rejected += resp.rejected;
			for (const r of resp.rejections) {
				result.rejections.push({
					operationId: r.operationId,
					reason: r.reason,
				});
			}

			const rejectedIds = new Set(
				resp.rejections.map((r) => r.operationId),
			);
			await this.settle(operations, rejectedIds);
		}

		// 2) Economy — one call per entry (each is its own transaction).
		for (const op of economy) {
			const payload = (op.payload ?? {}) as EconomyPayload;
			const currency: CurrencyWire = payload.currency ?? 'xp';
			const amount = payload.amount ?? amountFromPayload(op.payload);

			const resp = await this.client.applyEconomy({
				idempotencyKey: deriveIdempotencyKey(op),
				kind: economyKind(op.type),
				currency,
				amount,
				iapReceipt:
					op.type === 'economy.iap_purchase'
						? payload.receipt
						: undefined,
			});

			if (resp.status === 'applied' || resp.status === 'duplicate') {
				result.synced += 1;
				await this.confirm(op, 'synced');
			} else {
				// insufficient_funds | invalid_amount — a real rejection.
				result.rejected += 1;
				result.rejections.push({
					operationId: op.id,
					reason: resp.status,
				});
				await this.confirm(op, 'rejected', resp.status);
			}
		}

		return result;
	}

	/** Move a batch of ops out of the queue, marking each synced or rejected. */
	private async settle(
		ops: Operation[],
		rejectedIds: Set<string>,
	): Promise<void> {
		await this.s.transaction(
			[STORAGE_TABLES.SYNC_QUEUE, STORAGE_TABLES.OPERATIONS],
			async () => {
				for (const op of ops) {
					const rejected = rejectedIds.has(op.id);
					await this.s.set(STORAGE_TABLES.OPERATIONS, op.id, {
						...op,
						status: rejected ? 'rejected' : 'synced',
						serverTimestamp: op.serverTimestamp,
					});
					await this.s.delete(STORAGE_TABLES.SYNC_QUEUE, op.id);
				}
			},
		);
	}

	/** Confirm a single op (used by the economy path). */
	private async confirm(
		op: Operation,
		status: 'synced' | 'rejected',
		rejectReason?: string,
	): Promise<void> {
		await this.s.transaction(
			[STORAGE_TABLES.SYNC_QUEUE, STORAGE_TABLES.OPERATIONS],
			async () => {
				await this.s.set(STORAGE_TABLES.OPERATIONS, op.id, {
					...op,
					status,
					rejectReason,
				});
				await this.s.delete(STORAGE_TABLES.SYNC_QUEUE, op.id);
			},
		);
	}

	// ── pull ───────────────────────────────────────────────────────────────

	async pull(since?: number): Promise<SyncPullResult> {
		const result: SyncPullResult = { applied: 0, conflicts: 0 };

		if (!this.getUserId()) return result;
		if (!this.net.isOnline()) return result;

		let cursor =
			since ??
			(await this.s.get<number>(
				STORAGE_TABLES.SETTINGS,
				PULL_CURSOR_KEY,
			)) ??
			0;

		// Walk the server cursor until caught up. Bounded so a runaway server
		// can't loop us forever; 200 pages × 500 ops = 100k ops per sync.
		const MAX_PAGES = 200;
		for (let page = 0; page < MAX_PAGES; page++) {
			const resp = await this.client.pullOperations(cursor);
			const fromOthers = resp.operations.filter(
				(o) => o.deviceId !== this.deviceId,
			);

			if (fromOthers.length > 0) {
				await this.s.transaction(
					[STORAGE_TABLES.OPERATIONS],
					async () => {
						for (const o of fromOthers) {
							await this.s.set(STORAGE_TABLES.OPERATIONS, o.id, {
								...o,
								userId: this.getUserId() ?? '',
								status: 'synced',
							});
						}
					},
				);
				if (this.applyPulled) await this.applyPulled(fromOthers);
				result.applied += fromOthers.length;
			}

			// Advance the watermark to the furthest serverTimestamp we've seen.
			if (resp.operations.length > 0) {
				cursor =
					resp.operations[resp.operations.length - 1].serverTimestamp;
				await this.s.set(
					STORAGE_TABLES.SETTINGS,
					PULL_CURSOR_KEY,
					cursor,
				);
			}

			if (resp.nextSince == null) break; // caught up
			cursor = resp.nextSince;
		}

		return result;
	}

	// ── sync ───────────────────────────────────────────────────────────────

	async sync(): Promise<SyncResult> {
		// Collapse concurrent callers onto one run.
		if (this.inFlight) return this.inFlight;
		this.inFlight = this.runSync();
		try {
			return await this.inFlight;
		} finally {
			this.inFlight = null;
		}
	}

	private async runSync(): Promise<SyncResult> {
		const start = this.now();
		// Push first so our own work is on the server before we pull others'.
		const push = await this.push();
		const pull = await this.pull();
		return { push, pull, durationMs: this.now() - start };
	}

	// ── pendingCount ─────────────────────────────────────────────────────

	async pendingCount(): Promise<number> {
		return this.s.count(STORAGE_TABLES.SYNC_QUEUE);
	}
}

/**
 * Best-effort amount extraction for economy ops whose payload didn't carry an
 * explicit `amount`. Reward events typically encode the magnitude as `xp`,
 * `gems`, or `amount`. Returns 0 if none found — the server treats 0 as
 * invalid_amount, surfacing the bad op as a rejection rather than silently
 * crediting nothing.
 */
function amountFromPayload(payload: unknown): number {
	if (payload && typeof payload === 'object') {
		const p = payload as Record<string, unknown>;
		for (const k of ['amount', 'xp', 'gems', 'value']) {
			const v = p[k];
			if (typeof v === 'number' && Number.isFinite(v)) return Math.abs(v);
		}
	}
	return 0;
}
